// Config and hooks shared across the named `@github-ui/sanitize` presets.
//
// The link hook runs isomorphically: linkedom exposes `target`,
// so it hardens links on both the client and the server.

import {isFeatureEnabled} from '@github-ui/feature-flags'

import type {Config, Purifier, UponSanitizeAttributeHookEvent} from '../types'

// ── Base config shared across the presets ─────────────────────────────────────
// The default-deny posture every preset inherits. `ALLOW_DATA_ATTR: false` denies
// all `data-*` attributes; presets re-permit specific markers via their own
// `ADD_ATTR` / `ADD_URI_SAFE_ATTR` allowlists.
export const SHARED_CONFIG: Readonly<Config> = {
  FORBID_TAGS: ['style'],
  ALLOW_DATA_ATTR: false,
}

// ── Link hardening hook (always on) ───────────────────────────────────────────

// Ensure all sanitized links open in a new window.
// https://github.com/cure53/DOMPurify/tree/main/demos#hook-to-open-all-links-in-a-new-window-link
export const sanitizeNewWindowLinks = (node: Element) => {
  // set all elements owning target to target=_blank
  if ('target' in node || (typeof HTMLAnchorElement !== 'undefined' && node instanceof HTMLAnchorElement)) {
    node.setAttribute('target', '_blank')
    // prevent https://www.owasp.org/index.php/Reverse_Tabnabbing
    node.setAttribute('rel', 'noopener noreferrer')
  }
  // set non-HTML/MathML links to xlink:show=new
  // eslint-disable-next-line github/get-attribute
  if (!node.hasAttribute('target') && (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    // eslint-disable-next-line github/get-attribute
    node.setAttribute('xlink:show', 'new')
  }
}

// ── Class hook — the shared `js-*`-only denylist (github/react-platform#1728) ───────────────────────

/**
 * Strip the `js-*` class tokens GitHub's client behaviors bind to (selector
 * observers like `observe('.js-...')`), leaving every other class intact. A
 * `js-*`-only denylist rather than a wholesale class allowlist: every confirmed
 * bug-bounty payload (github/web-systems#4809) weaponizes a `js-*` hook, so this
 * neutralizes them while preserving legitimate rendered-markdown classes (syntax
 * highlighting like `highlight`/`blob-code`/`pl-*`, etc.).
 *
 * Class values are tokenized on ASCII whitespace exactly as the browser does
 * (space, tab, LF, FF, CR), so tab/newline/FF/CR-obfuscated `js-*` tokens are
 * isolated the same way a mutation-observer gadget would see them and cannot slip
 * through. The `js-` prefix match is case-insensitive for defense in depth.
 */
export const stripJsHookClasses = (_node: Element, data: UponSanitizeAttributeHookEvent): void => {
  if (data.attrName !== 'class') return

  const kept = data.attrValue.split(/[ \t\n\f\r]+/).filter(token => token !== '' && !/^js-/i.test(token))
  if (kept.length === 0) {
    data.keepAttr = false
  } else {
    data.attrValue = kept.join(' ')
  }
}

// ── Attribute hook — validate URI-safe `data-*` values (github/react-platform#1724) ─────────────────

// `data-*` attributes that carry a value which may legitimately contain a colon
// (Catalyst `data-action="event:tag#method"`, titles/JSON/hosts), so DOMPurify's
// URI heuristic would otherwise strip them. Presets opt them out of that heuristic
// via `ADD_URI_SAFE_ATTR` — which ALSO bypasses DOMPurify's scheme scrubbing — so
// every one of these MUST be re-validated by `validateUriSafeDataAttr` below.
export const URI_SAFE_DATA_ATTRS = new Set([
  'data-action',
  'data-item-title',
  'data-plain',
  'data-host',
  'data-content',
  'data-json',
])

// ASCII control chars + Unicode whitespace DOMPurify strips before scheme testing;
// mirrors DOMPurify's own `ATTR_WHITESPACE` so an obfuscated scheme (leading NULs,
// tabs, newlines) cannot evade the check.
// eslint-disable-next-line no-control-regex
const ATTR_WHITESPACE = /[\u0000-\u0020\u00a0\u1680\u180e\u2000-\u2029\u205f\u3000]/g

// Dangerous URL schemes DOMPurify normally scrubs. Mirrors DOMPurify's
// `IS_SCRIPT_OR_DATA`, extended with `vbscript`.
const IS_DANGEROUS_SCHEME = /^(?:\w+script|data|vbscript):/i

/**
 * Re-apply dangerous-scheme rejection to the `data-*` attributes opted out of
 * DOMPurify's URI heuristic via `ADD_URI_SAFE_ATTR`. That opt-out also bypasses
 * DOMPurify's scheme scrubbing, so without this a payload like
 * `data-content="javascript:…"` would survive and could be read back into a script
 * sink by a client behavior. The value is whitespace/control-stripped and lowercased
 * (matching DOMPurify) before testing its leading scheme; a match drops the attribute.
 * Legitimate values for these markers (Catalyst actions, titles, JSON, hostnames)
 * never lead with `javascript:`/`data:`/`vbscript:`, so this rejects only abuse.
 */
export const validateUriSafeDataAttr = (_node: Element, data: UponSanitizeAttributeHookEvent): void => {
  if (!URI_SAFE_DATA_ATTRS.has(data.attrName)) return

  const normalized = data.attrValue.replace(ATTR_WHITESPACE, '').toLowerCase()
  if (IS_DANGEROUS_SCHEME.test(normalized)) {
    data.keepAttr = false
  }
}

// ── Shared security-hook attachment ───────────────────────────────────────────

/**
 * Attach the always-on security hooks shared across the hardened sanitize paths:
 * the `js-*` class denylist
 * (github/react-platform#1728), and the URI-safe `data-*` scheme validator
 * (github/react-platform#1724). They run AFTER DOMPurify applies the config's
 * allowlist, so a caller cannot defeat them via `domPurifyConfig`.
 * The lax path attaches the same hooks via {@link applyLaxSecurityHooks}.
 */
export function applySecurityHooks(instance: Purifier): void {
  instance.addHook('uponSanitizeAttribute', stripJsHookClasses)
  instance.addHook('uponSanitizeAttribute', validateUriSafeDataAttr)
}

/**
 * Attach the shared security hooks to a lax (`github-sanitize` /
 * `github-sanitize-lax`) purifier so a caller's `domPurifyConfig` cannot clear them
 * (github/react-platform#1726). Applied to both lax entry points: the default
 * singleton (at module init) and every `createPurifier()` instance without a
 * self-hardening preset callback. Attached once per purifier, so they can't be
 * removed per call. Each hook guards on the `sanitize_dompurify_hardening` flag at
 * runtime — a safe no-op while hardening is off (lax stays legacy-permissive until
 * github/react-platform#1725 flips the gate; wiring that flag is out of scope here).
 * The runtime check also lets github/react-platform#1725 and tests toggle the gate on
 * an already-attached purifier. The two `uponSanitizeAttribute` hooks share one
 * registration so the flag is read once per attribute.
 */
export function applyLaxSecurityHooks(instance: Purifier): void {
  instance.addHook('uponSanitizeAttribute', (node, data) => {
    if (!isFeatureEnabled('sanitize_dompurify_hardening')) return
    stripJsHookClasses(node, data)
    validateUriSafeDataAttr(node, data)
  })
}
